<template>
  <div class="min-h-screen hs-page-backdrop">
    <div v-if="!embedded" class="px-6 pt-6">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <hs-button
          variant="secondary"
          icon="refresh-cw"
          :loading="isRefreshing"
          @click="refreshActiveTab"
        >
          Refresh
        </hs-button>
      </div>
    </div>

    <div class="">
      <div
        class="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
      >
        <hs-tabs :tabs="tabsWithTotals" v-model="activeTab" />

        <div class="mt-6 space-y-4">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex flex-wrap items-center gap-3">
              <hs-input
                v-model="currentFilters.search"
                class="w-64"
                type="search"
                icon="search"
                placeholder="Search by email, phone, or name..."
                @update:modelValue="handleSearchInput"
              />
              <hs-select
                v-model="currentFilters.verified"
                class="min-w-[180px]"
                :options="verificationStatusOptions"
                @update:modelValue="handleFilterChange"
              />
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400">
              Results update automatically as you change filters.
            </div>
          </div>

          <section v-if="activeTab === 'users'">
            <hs-table-shell title="User email verification" description="Review user email status and send or reset verification actions.">
              <div v-if="userLoading" class="admin-email-table__skeleton">
                <div v-for="idx in 5" :key="`user-skeleton-${idx}`"></div>
              </div>
              <hs-empty-state
                v-else-if="userRows.length === 0"
                icon="mail"
                title="No users found"
                description="Adjust your filters or search term."
              />
              <hs-data-table
                v-else
                :headers="userHeaders"
                :data="userTableRows"
                :header-key-mapping="userHeaderKeyMapping"
              >
                <template #cell-id="{ value }">
                  <span class="admin-email-table__mono">{{ value }}</span>
                </template>
                <template #cell-user="{ row }">
                  <div class="admin-email-table__primary">{{ row.user }}</div>
                  <div class="admin-email-table__muted">Phone: {{ row.phone }}</div>
                </template>
                <template #cell-email="{ value }">
                  <span class="admin-email-table__strong">{{ value }}</span>
                </template>
                <template #cell-status="{ row }">
                  <hs-badge :tone="row.__raw.email_verified ? 'success' : 'warning'">
                    {{ row.status }}
                  </hs-badge>
                </template>
                <template #cell-actions="{ row }">
                  <div class="admin-email-table__actions">
                    <hs-action-menu
                      :items="getUserActions(row.__raw)"
                      label="Actions"
                      icon="more-horizontal"
                      variant="secondary"
                      size="sm"
                      @select="handleActionMenuSelect"
                    />
                  </div>
                </template>
              </hs-data-table>
            </hs-table-shell>
            <div v-if="userPagination.total_pages > 1" class="mt-4">
              <hs-pagination
                :current-page="userPagination.page"
                :total-pages="userPagination.total_pages"
                :total-items="userPagination.total"
                :items-per-page="userPagination.per_page"
                :per-page-options="[10, 20, 25, 50, 100]"
                @previous-page="changeUserPage(userPagination.page - 1)"
                @next-page="changeUserPage(userPagination.page + 1)"
                @update:itemsPerPage="handleUserPerPageChange"
              />
            </div>
          </section>

          <section v-else>
            <hs-table-shell title="Store email verification" description="Review store contact data, ownership, and verification actions.">
              <div v-if="storeLoading" class="admin-email-table__skeleton">
                <div v-for="idx in 5" :key="`store-skeleton-${idx}`"></div>
              </div>
              <hs-empty-state
                v-else-if="storeRows.length === 0"
                icon="shopping-bag"
                title="No stores found"
                description="Adjust your filters or search term."
              />
              <hs-data-table
                v-else
                :headers="storeHeaders"
                :data="storeTableRows"
                :header-key-mapping="storeHeaderKeyMapping"
              >
                <template #cell-id="{ value }">
                  <span class="admin-email-table__mono">{{ value }}</span>
                </template>
                <template #cell-store="{ row }">
                  <div class="admin-email-table__primary">{{ row.store }}</div>
                  <div class="admin-email-table__muted">Merchant: {{ row.merchant }}</div>
                </template>
                <template #cell-contact="{ row }">
                  <div class="admin-email-table__strong">{{ row.email }}</div>
                  <div class="admin-email-table__muted">{{ row.phone }}</div>
                </template>
                <template #cell-owner="{ row }">
                  <div>{{ row.owner }}</div>
                  <div class="admin-email-table__muted">{{ row.group }}</div>
                </template>
                <template #cell-status="{ row }">
                  <hs-badge :tone="row.__raw.email_verified ? 'success' : 'warning'">
                    {{ row.status }}
                  </hs-badge>
                </template>
                <template #cell-last_updated="{ row }">
                  <div>{{ row.updatedAt }}</div>
                  <div class="admin-email-table__muted">{{ row.createdAt }}</div>
                </template>
                <template #cell-actions="{ row }">
                  <div class="admin-email-table__actions">
                    <hs-button
                      variant="secondary"
                      size="sm"
                      icon="settings"
                      @click="openStoreManagementModal(row.__raw)"
                    >
                      Manage
                    </hs-button>
                  </div>
                </template>
              </hs-data-table>
            </hs-table-shell>
            <div v-if="storePagination.total_pages > 1" class="mt-4">
              <hs-pagination
                :current-page="storePagination.page"
                :total-pages="storePagination.total_pages"
                :total-items="storePagination.total"
                :items-per-page="storePagination.per_page"
                :per-page-options="[10, 20, 25, 50, 100]"
                @previous-page="changeStorePage(storePagination.page - 1)"
                @next-page="changeStorePage(storePagination.page + 1)"
                @update:itemsPerPage="handleStorePerPageChange"
              />
            </div>
          </section>
        </div>
      </div>
    </div>

    <email-edit-modal
      v-if="editContext"
      :entity-label="editContext.type === 'user' ? 'user email' : 'store email'"
      :entity-name="editContext.label"
      :current-email="editContext.email"
      :loading="editContext.loading"
      :server-error="editContext.error"
      @close="closeEditModal"
      @save="saveEmailChange"
    />

    <confirmation-modal
      :is-open="removeContext !== null"
      :title="removeContext?.title || ''"
      :message="removeContext?.message || ''"
      confirm-text="Remove email"
      cancel-text="Cancel"
      type="danger"
      :loading="removeContext?.loading || false"
      @confirm="confirmRemoveEmail"
      @close="removeContext = null"
    />

    <ebm-initialize-modal
      v-if="ebmReinitContext"
      :store-id="ebmReinitContext.storeId"
      :store-name="ebmReinitContext.storeName"
      :ebm-settings="ebmReinitContext.ebmSettings"
      :loading="ebmReinitContext.loading"
      :loading-message="ebmReinitContext.loadingMessage"
      :error="ebmReinitContext.error"
      :error-details="ebmReinitContext.errorDetails"
      :success-message="ebmReinitContext.successMessage"
      :response-details="ebmReinitContext.responseDetails"
      @close="closeEbmInitModal"
      @confirm="confirmEbmInitialize"
    />


    <hs-modal
      v-if="storeManagementContext"
      :model-value="true"
      title="Manage store"
      :description="storeManagementContext.name || `Store #${storeManagementContext.id}`"
      size="lg"
      @close="closeStoreManagementModal"
    >
      <div class="store-management-modal">
        <hs-tabs :tabs="storeManagementTabs" v-model="storeManagementTab" />

        <section v-if="storeManagementTab === 'overview'" class="store-management-modal__section">
          <div class="owner-details__grid">
            <div class="owner-details__item">
              <span>Store ID</span>
              <strong>{{ formatValue(storeManagementContext.id) }}</strong>
            </div>
            <div class="owner-details__item">
              <span>Name</span>
              <strong>{{ formatValue(storeManagementContext.name) }}</strong>
            </div>
            <div class="owner-details__item">
              <span>Merchant</span>
              <strong>{{ formatValue(storeManagementContext.merchant_code) }}</strong>
            </div>
            <div class="owner-details__item">
              <span>Type</span>
              <strong>{{ formatStoreType(storeManagementContext.store_type) }}</strong>
            </div>
            <div class="owner-details__item">
              <span>Currency</span>
              <strong>{{ formatValue(storeManagementContext.store_currency) }}</strong>
            </div>
            <div class="owner-details__item">
              <span>Created</span>
              <strong>{{ formatDate(storeManagementContext.created_at) }}</strong>
            </div>
          </div>
          <div class="owner-details__item owner-details__item--wide">
            <span>Location</span>
            <strong>{{ formatValue(storeManagementContext.location) }}</strong>
            <small>Exact service match: {{ formatBoolean(storeManagementContext.service_name_exact_match) }}</small>
          </div>
        </section>

        <section v-else-if="storeManagementTab === 'owner'" class="store-management-modal__section">
          <div class="store-management-modal__summary">
            <span>Current owner</span>
            <strong>Owner #{{ formatValue(storeManagementContext.owner_id) }}</strong>
          </div>

          <div class="store-management-inline-card">
            <div class="store-management-inline-card__header">
              <div>
                <h4>Owner details</h4>
                <p>Loaded automatically from the current store owner account.</p>
              </div>
            </div>

            <div v-if="ownerDetailsContext?.loading" class="owner-details__state">
              Loading owner details...
            </div>

            <div v-else-if="ownerDetailsContext?.error" class="owner-details__state owner-details__state--error">
              {{ ownerDetailsContext.error }}
            </div>

            <div v-else-if="ownerDetailsContext?.user" class="owner-details__grid">
              <div class="owner-details__item">
                <span>Owner ID</span>
                <strong>{{ formatValue(ownerDetailsContext.user.id) }}</strong>
              </div>
              <div class="owner-details__item">
                <span>Status</span>
                <hs-badge :tone="ownerDetailsContext.user.email_verified ? 'success' : 'warning'">
                  {{ ownerDetailsContext.user.email_verified ? 'Email verified' : 'Email pending' }}
                </hs-badge>
              </div>
              <div class="owner-details__item">
                <span>Name</span>
                <strong>{{ formatValue(ownerDetailsContext.user.names) }}</strong>
              </div>
              <div class="owner-details__item">
                <span>Phone</span>
                <strong>{{ formatValue(ownerDetailsContext.user.phone_number) }}</strong>
              </div>
              <div class="owner-details__item owner-details__item--wide">
                <span>Email</span>
                <strong>{{ formatValue(ownerDetailsContext.user.email) }}</strong>
                <small>Last email update: {{ formatDate(ownerDetailsContext.user.email_verified_at) }}</small>
              </div>
            </div>
          </div>

          <div class="store-management-inline-card">
            <div class="store-management-inline-card__header">
              <div>
                <h4>Transfer ownership</h4>
                <p>Look up an existing HovaStore user by phone number, then transfer this store to that account.</p>
              </div>
            </div>

            <div class="store-management-inline-form">
              <hs-input
                v-model.trim="transferPhoneNumber"
                type="tel"
                label="New owner phone number"
                icon="phone"
                placeholder="+250..."
                @keyup.enter="lookupTransferOwner"
              />
              <hs-button
                variant="secondary"
                icon="search"
                :loading="transferLookupLoading"
                :disabled="transferLookupLoading || !transferPhoneNumber"
                @click="lookupTransferOwner"
              >
                Look up
              </hs-button>
            </div>

            <div v-if="transferLookupError" class="store-management-alert store-management-alert--danger">
              {{ transferLookupError }}
            </div>

            <div v-if="transferResolvedUser" class="store-management-user-card">
              <div class="store-management-user-card__avatar">
                {{ transferResolvedUserInitials }}
              </div>
              <div>
                <strong>{{ transferResolvedUserDisplayName }}</strong>
                <span>{{ formatValue(transferResolvedUser.phone_number) }}</span>
                <small>{{ formatValue(transferResolvedUser.email) }}</small>
              </div>
              <hs-badge :tone="transferUserIsCurrentOwner ? 'warning' : 'success'">
                {{ transferUserIsCurrentOwner ? 'Current owner' : 'Ready' }}
              </hs-badge>
            </div>

            <div v-if="transferServerError" class="store-management-alert store-management-alert--danger">
              {{ transferServerError }}
            </div>

            <div class="store-management-inline-actions">
              <hs-button
                variant="danger"
                icon="refresh-cw"
                :loading="transferringOwnership"
                :disabled="!canTransferOwnership"
                @click="submitOwnershipTransfer"
              >
                Transfer ownership
              </hs-button>
            </div>
          </div>
        </section>

        <section v-else-if="storeManagementTab === 'email'" class="store-management-modal__section">
          <div class="owner-details__grid">
            <div class="owner-details__item">
              <span>Email</span>
              <strong>{{ formatValue(storeManagementContext.email) }}</strong>
            </div>
            <div class="owner-details__item">
              <span>Status</span>
              <hs-badge :tone="storeManagementContext.email_verified ? 'success' : 'warning'">
                {{ storeManagementContext.email_verified ? 'Verified' : 'Pending' }}
              </hs-badge>
            </div>
            <div class="owner-details__item">
              <span>Phone</span>
              <strong>{{ formatValue(storeManagementContext.phone_number) }}</strong>
            </div>
            <div class="owner-details__item">
              <span>Updated</span>
              <strong>{{ formatDate(storeManagementContext.email_verified_at) }}</strong>
            </div>
          </div>

          <div class="store-management-inline-card">
            <div class="store-management-inline-card__header">
              <div>
                <h4>Store email</h4>
                <p>Update the contact email, send a verification code, or remove the address.</p>
              </div>
            </div>

            <div class="store-management-inline-form store-management-inline-form--three">
              <hs-input
                v-model.trim="storeEmailDraft"
                type="email"
                label="Email address"
                icon="mail"
                placeholder="store@example.com"
              />
              <hs-button
                icon="save"
                :loading="storeEmailSaving"
                :disabled="storeEmailSaving"
                @click="saveManagedStoreEmail"
              >
                Save
              </hs-button>
              <hs-button
                variant="secondary"
                icon="send"
                :loading="storeActionLoading === storeManagementContext.id"
                :disabled="storeActionLoading === storeManagementContext.id || !storeManagementContext.email"
                @click="sendManagedStoreVerification"
              >
                Send code
              </hs-button>
            </div>

            <div v-if="storeEmailError" class="store-management-alert store-management-alert--danger">
              {{ storeEmailError }}
            </div>

            <div class="store-management-danger-zone">
              <template v-if="!confirmStoreEmailRemoval">
                <span>Remove the store email when the address is no longer valid.</span>
                <hs-button
                  variant="danger"
                  size="sm"
                  icon="trash-2"
                  :disabled="!storeManagementContext.email"
                  @click="confirmStoreEmailRemoval = true"
                >
                  Remove email
                </hs-button>
              </template>
              <template v-else>
                <span>Are you sure you want to remove this store email?</span>
                <div class="store-management-danger-zone__actions">
                  <hs-button variant="secondary" size="sm" @click="confirmStoreEmailRemoval = false">
                    Cancel
                  </hs-button>
                  <hs-button
                    variant="danger"
                    size="sm"
                    icon="trash-2"
                    :loading="storeEmailRemoving"
                    @click="removeManagedStoreEmail"
                  >
                    Confirm
                  </hs-button>
                </div>
              </template>
            </div>
          </div>
        </section>


        <section v-else-if="storeManagementTab === 'operations'" class="store-management-modal__section">
          <div class="owner-details__grid">
            <div class="owner-details__item">
              <span>Timezone</span>
              <strong>{{ formatTimezone(storeManagementContext) }}</strong>
            </div>
            <div class="owner-details__item">
              <span>Inventory alert</span>
              <strong>{{ formatAlertHour(storeManagementContext.inventory_alert_send_hour) }}</strong>
            </div>
            <div class="owner-details__item">
              <span>MTN MoMo</span>
              <strong>{{ formatValue(storeManagementContext.mtn_momo_code) }}</strong>
            </div>
            <div class="owner-details__item">
              <span>Airtel Money</span>
              <strong>{{ formatValue(storeManagementContext.airtel_money_code) }}</strong>
            </div>
          </div>
          <div class="store-management-modal__actions-grid">
            <button type="button" class="store-management-action" @click="openEbmInitStandalone(storeManagementContext)">
              <hs-icon name="refresh-cw" :size="18" />
              <span>
                <strong>Initialize EBM</strong>
                <small>Prepare or retry RRA EBM setup for this store.</small>
              </span>
            </button>
          </div>
        </section>
      </div>

      <template #footer>
        <hs-button variant="secondary" @click="closeStoreManagementModal">Close</hs-button>
      </template>
    </hs-modal>



    <hs-modal
      v-if="storeDetailsContext"
      :model-value="true"
      title="Store details"
      :description="storeDetailsContext.name || `Store #${storeDetailsContext.id}`"
      size="md"
      @close="closeStoreDetailsModal"
    >
      <div class="owner-details">
        <div class="owner-details__grid">
          <div class="owner-details__item">
            <span>Store ID</span>
            <strong>{{ formatValue(storeDetailsContext.id) }}</strong>
          </div>
          <div class="owner-details__item">
            <span>Merchant</span>
            <strong>{{ formatValue(storeDetailsContext.merchant_code) }}</strong>
          </div>
          <div class="owner-details__item">
            <span>Type</span>
            <strong>{{ formatStoreType(storeDetailsContext.store_type) }}</strong>
          </div>
          <div class="owner-details__item">
            <span>Currency</span>
            <strong>{{ formatValue(storeDetailsContext.store_currency) }}</strong>
          </div>
          <div class="owner-details__item">
            <span>MTN MoMo</span>
            <strong>{{ formatValue(storeDetailsContext.mtn_momo_code) }}</strong>
          </div>
          <div class="owner-details__item">
            <span>Airtel Money</span>
            <strong>{{ formatValue(storeDetailsContext.airtel_money_code) }}</strong>
          </div>
          <div class="owner-details__item">
            <span>Timezone</span>
            <strong>{{ formatTimezone(storeDetailsContext) }}</strong>
          </div>
          <div class="owner-details__item">
            <span>Inventory alert</span>
            <strong>{{ formatAlertHour(storeDetailsContext.inventory_alert_send_hour) }}</strong>
          </div>
        </div>

        <div class="owner-details__item owner-details__item--wide">
          <span>Location</span>
          <strong>{{ formatValue(storeDetailsContext.location) }}</strong>
          <small>Exact service match: {{ formatBoolean(storeDetailsContext.service_name_exact_match) }}</small>
        </div>
      </div>

      <template #footer>
        <hs-button variant="secondary" @click="closeStoreDetailsModal">Close</hs-button>
      </template>
    </hs-modal>
  </div>
</template>

<script>
import { computed, reactive, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import HsPagination from "@/components/table/HSPagination.vue";
import EmailEditModal from "@/components/admin/email/EmailEditModal.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import EbmInitializeModal from "@/components/admin/EbmInitializeModal.vue";
import http from "@/helper/http";
import { getStoreEbmSettings, initializeEbmForStore } from "@/helper/ebmApi";
import {
  HsBadge,
  HsActionMenu,
  HsButton,
  HsDataTable,
  HsEmptyState,
  HsInput,
  HsIcon,
  HsModal,
  HsSelect,
  HsTableShell,
  HsTabs,
} from "@/components/ui";

const baseTabs = [
  { id: "users", label: "Users", icon: "users" },
  { id: "stores", label: "Stores", icon: "shopping-bag" },
];

const defaultFilters = () => ({
  search: "",
  verified: "all",
});

export default {
  name: "AdminEmailVerificationView",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HsButton,
    HsBadge,
    HsActionMenu,
    HsDataTable,
    HsEmptyState,
    HsInput,
    HsIcon,
    HsModal,
    HsSelect,
    HsTableShell,
    HsTabs,
    HsPagination,
    EmailEditModal,
    ConfirmationModal,
    EbmInitializeModal,
  },
  setup() {
    const activeTab = ref("users");
    const isRefreshing = ref(false);
    const verificationStatusOptions = [
      { label: "All statuses", value: "all" },
      { label: "Verified only", value: "true" },
      { label: "Unverified only", value: "false" },
    ];

    const currentFilters = reactive(defaultFilters());
    const debouncedSearchTimeout = ref(null);

    const userRows = ref([]);
    const userLoading = ref(false);
    const userPagination = reactive({
      page: 1,
      per_page: 20,
      total: 0,
      total_pages: 1,
    });
    const userActionLoading = ref(null);

    const storeRows = ref([]);
    const storeLoading = ref(false);
    const storePagination = reactive({
      page: 1,
      per_page: 20,
      total: 0,
      total_pages: 1,
    });
    const storeActionLoading = ref(null);

    const tabsWithTotals = computed(() =>
      baseTabs.map((tab) => {
        const total =
          tab.id === "users" ? userPagination.total : storePagination.total;
        return {
          ...tab,
          label: total > 0 ? `${tab.label} (${total})` : tab.label,
        };
      })
    );

    const editContext = ref(null);
    const removeContext = ref(null);
    const ebmReinitContext = ref(null);
    const ownerDetailsContext = ref(null);
    const storeDetailsContext = ref(null);
    const storeManagementContext = ref(null);
    const storeManagementTab = ref("overview");
    const storeEmailDraft = ref("");
    const storeEmailError = ref("");
    const storeEmailSaving = ref(false);
    const storeEmailRemoving = ref(false);
    const confirmStoreEmailRemoval = ref(false);
    const transferPhoneNumber = ref("");
    const transferResolvedUser = ref(null);
    const transferLookupLoading = ref(false);
    const transferLookupError = ref("");
    const transferServerError = ref("");
    const transferringOwnership = ref(false);
    const storeManagementTabs = [
      { id: "overview", label: "Overview", icon: "info" },
      { id: "owner", label: "Owner", icon: "user" },
      { id: "email", label: "Email", icon: "mail" },
      { id: "operations", label: "Operations", icon: "settings" },
    ];
    const transferResolvedUserDisplayName = computed(() => {
      const user = transferResolvedUser.value;
      if (!user) {
        return "";
      }
      return user.names || user.phone_number || `User #${user.id}`;
    });
    const transferResolvedUserInitials = computed(() => {
      const label = transferResolvedUserDisplayName.value || "U";
      return label
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "U";
    });
    const transferUserIsCurrentOwner = computed(() => {
      const ownerId = Number(storeManagementContext.value?.owner_id);
      const userId = Number(transferResolvedUser.value?.id);
      return Boolean(ownerId && userId && ownerId === userId);
    });
    const canTransferOwnership = computed(() =>
      Boolean(
        storeManagementContext.value?.id &&
          transferResolvedUser.value?.id &&
          !transferUserIsCurrentOwner.value &&
          !transferringOwnership.value
      )
    );
    const userHeaders = ["ID", "User", "Email", "Status", "Last updated", "Actions"];
    const userHeaderKeyMapping = {
      ID: "id",
      User: "user",
      Email: "email",
      Status: "status",
      "Last updated": "updatedAt",
      Actions: "actions",
    };
    const storeHeaders = [
      "ID",
      "Store",
      "Contact",
      "Owner",
      "Status",
      "Last updated",
      "Actions",
    ];
    const storeHeaderKeyMapping = {
      ID: "id",
      Store: "store",
      Contact: "contact",
      Owner: "owner",
      Status: "status",
      "Last updated": "updatedAt",
      Actions: "actions",
    };

    const loadUsers = async () => {
      userLoading.value = true;
      try {
        const params = {
          page: userPagination.page,
          per_page: userPagination.per_page,
        };
        if (currentFilters.search) {
          params.search = currentFilters.search;
        }
        if (currentFilters.verified !== "all") {
          params.verified = currentFilters.verified;
        }
        const response = await http.get("/v1/admin/email-verification/users", {
          params,
        });
        const payload = response?.data || {};
        userRows.value = Array.isArray(payload.items) ? payload.items : [];
        userPagination.total = payload.total ?? payload.total_items ?? 0;
        userPagination.total_pages = payload.total_pages ?? payload.pages ?? 1;
      } catch (error) {
        console.error("Failed to load users", error);
        toast.error(
          error?.response?.data?.error?.message ||
            "Unable to load user email verification data."
        );
        userRows.value = [];
      } finally {
        userLoading.value = false;
      }
    };

    const loadStores = async () => {
      storeLoading.value = true;
      try {
        const params = {
          page: storePagination.page,
          per_page: storePagination.per_page,
        };
        if (currentFilters.search) {
          params.search = currentFilters.search;
        }
        if (currentFilters.verified !== "all") {
          params.verified = currentFilters.verified;
        }

        const response = await http.get("/v1/admin/email-verification/stores", {
          params,
        });
        const payload = response?.data || {};
        storeRows.value = Array.isArray(payload.items) ? payload.items : [];
        storePagination.total = payload.total ?? payload.total_items ?? 0;
        storePagination.total_pages = payload.total_pages ?? payload.pages ?? 1;
      } catch (error) {
        console.error("Failed to load stores", error);
        toast.error(
          error?.response?.data?.error?.message ||
            "Unable to load store email verification data."
        );
        storeRows.value = [];
      } finally {
        storeLoading.value = false;
      }
    };

    const refreshActiveTab = async () => {
      isRefreshing.value = true;
      try {
        if (activeTab.value === "users") {
          await loadUsers();
        } else {
          await loadStores();
        }
      } finally {
        isRefreshing.value = false;
      }
    };

    const handleSearchInput = () => {
      if (debouncedSearchTimeout.value) {
        clearTimeout(debouncedSearchTimeout.value);
      }
      debouncedSearchTimeout.value = setTimeout(() => {
        if (activeTab.value === "users") {
          userPagination.page = 1;
          loadUsers();
        } else {
          storePagination.page = 1;
          loadStores();
        }
      }, 300);
    };

    const handleFilterChange = () => {
      if (activeTab.value === "users") {
        userPagination.page = 1;
        loadUsers();
      } else {
        storePagination.page = 1;
        loadStores();
      }
    };

    const changeUserPage = (page) => {
      if (
        page === userPagination.page ||
        page < 1 ||
        page > userPagination.total_pages
      ) {
        return;
      }
      userPagination.page = page;
      loadUsers();
    };

    const changeStorePage = (page) => {
      if (
        page === storePagination.page ||
        page < 1 ||
        page > storePagination.total_pages
      ) {
        return;
      }
      storePagination.page = page;
      loadStores();
    };

    const handleUserPerPageChange = (newPerPage) => {
      userPagination.per_page = newPerPage;
      userPagination.page = 1; // Reset to first page when changing items per page
      loadUsers();
    };

    const handleStorePerPageChange = (newPerPage) => {
      storePagination.per_page = newPerPage;
      storePagination.page = 1; // Reset to first page when changing items per page
      loadStores();
    };

    const requestUserVerification = async (user) => {
      if (!user?.id) {
        return;
      }
      userActionLoading.value = user.id;
      try {
        await http.post(
          `/v1/admin/email-verification/users/${user.id}/request`
        );
        toast.success("Verification email sent to user.");
      } catch (error) {
        console.error("Failed to request user verification", error);
        toast.error(
          error?.response?.data?.error?.message ||
            "Unable to send verification email."
        );
      } finally {
        userActionLoading.value = null;
      }
    };

    const requestStoreVerification = async (store) => {
      if (!store?.id) {
        return;
      }
      storeActionLoading.value = store.id;
      try {
        await http.post(
          `/v1/admin/email-verification/stores/${store.id}/request`
        );
        toast.success("Verification email sent to store.");
      } catch (error) {
        console.error("Failed to request store verification", error);
        toast.error(
          error?.response?.data?.error?.message ||
            "Unable to send verification email."
        );
      } finally {
        storeActionLoading.value = null;
      }
    };

    const openEditUserEmail = (user) => {
      editContext.value = {
        type: "user",
        id: user.id,
        email: user.email,
        label: user.names ? `${user.names} (#${user.id})` : `User #${user.id}`,
        loading: false,
        error: "",
      };
    };

    const openEditStoreEmail = (store) => {
      editContext.value = {
        type: "store",
        id: store.id,
        email: store.email,
        label: store.name
          ? `${store.name} (#${store.id})`
          : `Store #${store.id}`,
        loading: false,
        error: "",
      };
    };

    const loadManagedStoreOwner = async (store) => {
      ownerDetailsContext.value = {
        storeId: store?.id,
        storeName: store?.name ? `${store.name} (#${store.id})` : `Store #${store?.id}`,
        ownerId: store?.owner_id,
        loading: true,
        error: "",
        user: null,
      };

      if (!store?.owner_id) {
        ownerDetailsContext.value.loading = false;
        ownerDetailsContext.value.error = "This store does not have an owner ID.";
        return;
      }

      try {
        const response = await http.get("/v1/admin/email-verification/users", {
          params: {
            "id[eq]": String(store.owner_id),
            page: 1,
            per_page: 1,
          },
        });
        const payload = response?.data || {};
        const users = Array.isArray(payload.items) ? payload.items : [];
        const owner = users.find((user) => user.id === store.owner_id) || null;

        if (!owner) {
          ownerDetailsContext.value.error =
            `Owner #${store.owner_id} was not found in admin users.`;
          return;
        }

        ownerDetailsContext.value.user = owner;
      } catch (error) {
        console.error("Failed to load owner details", error);
        ownerDetailsContext.value.error =
          error?.response?.data?.error?.message ||
          "Unable to load owner details.";
      } finally {
        if (ownerDetailsContext.value) {
          ownerDetailsContext.value.loading = false;
        }
      }
    };

    const resetTransferOwnershipForm = () => {
      transferPhoneNumber.value = "";
      transferResolvedUser.value = null;
      transferLookupError.value = "";
      transferServerError.value = "";
    };

    const resetManagedStoreEmailForm = (store = null) => {
      const source = store || storeManagementContext.value || {};
      storeEmailDraft.value = source.email || "";
      storeEmailError.value = "";
      confirmStoreEmailRemoval.value = false;
    };

    const updateManagedStore = (patch) => {
      if (!storeManagementContext.value) {
        return;
      }
      storeManagementContext.value = {
        ...storeManagementContext.value,
        ...patch,
      };
    };

    const openStoreManagementModal = (store) => {
      storeManagementContext.value = store;
      storeManagementTab.value = "overview";
      resetManagedStoreEmailForm(store);
      resetTransferOwnershipForm();
    };

    const closeStoreManagementModal = () => {
      storeManagementContext.value = null;
      ownerDetailsContext.value = null;
      resetManagedStoreEmailForm({});
      resetTransferOwnershipForm();
    };

    const openStoreDetailsModal = (store) => {
      storeDetailsContext.value = store;
    };

    const closeStoreDetailsModal = () => {
      storeDetailsContext.value = null;
    };

    const lookupTransferOwner = async () => {
      const phoneNumber = transferPhoneNumber.value.trim();
      transferResolvedUser.value = null;
      transferLookupError.value = "";
      transferServerError.value = "";

      if (!phoneNumber) {
        transferLookupError.value = "Enter a phone number to look up.";
        return;
      }

      transferLookupLoading.value = true;
      try {
        const response = await http.get("/v1/admin/jack/user/lookup", {
          params: { phone_number: phoneNumber },
        });
        const user = response?.data?.user || null;
        if (!user) {
          transferLookupError.value = "No user found with that phone number.";
          return;
        }
        transferResolvedUser.value = user;
      } catch (error) {
        console.error("Failed to look up transfer owner", error);
        transferLookupError.value =
          error?.response?.data?.error?.message ||
          "Unable to find a user with that phone number.";
      } finally {
        transferLookupLoading.value = false;
      }
    };

    const submitOwnershipTransfer = async () => {
      if (!canTransferOwnership.value) {
        return;
      }

      transferringOwnership.value = true;
      transferServerError.value = "";
      try {
        const response = await http.post(
          `/v1/admin/stores/${storeManagementContext.value.id}/transfer-ownership`,
          { new_owner_id: transferResolvedUser.value.id }
        );
        const updatedStore = response?.data?.store || {};
        updateManagedStore(updatedStore);
        toast.success(
          `Ownership of "${updatedStore?.name || storeManagementContext.value?.name || "store"}" transferred successfully.`
        );
        resetTransferOwnershipForm();
        await loadStores();
      } catch (error) {
        console.error("Failed to transfer store ownership", error);
        transferServerError.value =
          error?.response?.data?.error?.message ||
          "Unable to transfer store ownership.";
      } finally {
        transferringOwnership.value = false;
      }
    };

    const saveManagedStoreEmail = async () => {
      if (!storeManagementContext.value?.id) {
        return;
      }

      storeEmailSaving.value = true;
      storeEmailError.value = "";
      try {
        const email = storeEmailDraft.value.trim();
        await http.put(
          `/v1/admin/email-verification/stores/${storeManagementContext.value.id}/email`,
          { email }
        );
        updateManagedStore({
          email,
          email_verified: false,
          email_verified_at: null,
        });
        toast.success("Store email updated.");
        await loadStores();
      } catch (error) {
        console.error("Failed to update managed store email", error);
        storeEmailError.value =
          error?.response?.data?.error?.message ||
          "Unable to update store email address.";
      } finally {
        storeEmailSaving.value = false;
      }
    };

    const sendManagedStoreVerification = async () => {
      if (!storeManagementContext.value) {
        return;
      }
      await requestStoreVerification(storeManagementContext.value);
    };

    const removeManagedStoreEmail = async () => {
      if (!storeManagementContext.value?.id) {
        return;
      }

      storeEmailRemoving.value = true;
      storeEmailError.value = "";
      try {
        await http.delete(
          `/v1/admin/email-verification/stores/${storeManagementContext.value.id}/email`
        );
        updateManagedStore({
          email: null,
          email_verified: false,
          email_verified_at: null,
        });
        storeEmailDraft.value = "";
        confirmStoreEmailRemoval.value = false;
        toast.success("Store email removed.");
        await loadStores();
      } catch (error) {
        console.error("Failed to remove managed store email", error);
        storeEmailError.value =
          error?.response?.data?.error?.message ||
          "Unable to remove store email address.";
      } finally {
        storeEmailRemoving.value = false;
      }
    };

    const closeEditModal = () => {
      editContext.value = null;
    };

    const saveEmailChange = async (newEmail) => {
      if (!editContext.value) {
        return;
      }
      editContext.value.loading = true;
      editContext.value.error = "";
      try {
        if (editContext.value.type === "user") {
          await http.put(
            `/v1/admin/email-verification/users/${editContext.value.id}/email`,
            { email: newEmail }
          );
          toast.success("User email updated.");
          await loadUsers();
        } else {
          await http.put(
            `/v1/admin/email-verification/stores/${editContext.value.id}/email`,
            { email: newEmail }
          );
          toast.success("Store email updated.");
          await loadStores();
        }
        closeEditModal();
      } catch (error) {
        console.error("Failed to update email", error);
        editContext.value.error =
          error?.response?.data?.error?.message ||
          "Unable to update email address.";
      } finally {
        if (editContext.value) {
          editContext.value.loading = false;
        }
      }
    };

    const openRemoveUserEmail = (user) => {
      removeContext.value = {
        type: "user",
        id: user.id,
        message: `Remove email address for ${
          user.names || `user #${user.id}`
        }?`,
        title: "Remove user email",
        loading: false,
      };
    };

    const openRemoveStoreEmail = (store) => {
      removeContext.value = {
        type: "store",
        id: store.id,
        message: `Remove email address for ${
          store.name || `store #${store.id}`
        }?`,
        title: "Remove store email",
        loading: false,
      };
    };

    const confirmRemoveEmail = async () => {
      if (!removeContext.value) {
        return;
      }
      removeContext.value.loading = true;
      try {
        if (removeContext.value.type === "user") {
          await http.delete(
            `/v1/admin/email-verification/users/${removeContext.value.id}/email`
          );
          toast.success("User email removed.");
          await loadUsers();
        } else {
          await http.delete(
            `/v1/admin/email-verification/stores/${removeContext.value.id}/email`
          );
          toast.success("Store email removed.");
          await loadStores();
        }
        removeContext.value = null;
      } catch (error) {
        console.error("Failed to remove email", error);
        toast.error(
          error?.response?.data?.error?.message ||
            "Unable to remove email address."
        );
        removeContext.value.loading = false;
      }
    };

    watch(
      () => activeTab.value,
      (tab) => {
        if (tab === "users") {
          loadUsers();
        } else {
          loadStores();
        }
      },
      { immediate: true }
    );

    watch(
      [storeManagementTab, storeManagementContext],
      ([tab, store]) => {
        if (tab === "owner" && store?.id) {
          loadManagedStoreOwner(store);
        }
      }
    );

    const currentListLoading = computed(() =>
      activeTab.value === "users" ? userLoading.value : storeLoading.value
    );
    const userTableRows = computed(() =>
      userRows.value.map((user) => ({
        __raw: user,
        id: user.id,
        user: user.names || `User #${user.id}`,
        phone: user.phone_number || "—",
        email: user.email || "Not set",
        status: user.email_verified ? "Verified" : "Pending",
        updatedAt: formatDate(user.email_verified_at),
        actions: "",
      }))
    );
    const storeTableRows = computed(() =>
      storeRows.value.map((store) => ({
        __raw: store,
        id: store.id,
        store: store.name || `Store #${store.id}`,
        merchant: store.merchant_code || "—",
        contact: store.email || "Not set",
        phone: formatValue(store.phone_number),
        location: formatValue(store.location),
        type: formatStoreType(store.store_type),
        currency: formatValue(store.store_currency),
        payments: `MTN: ${formatValue(store.mtn_momo_code)}`,
        airtel: `Airtel: ${formatValue(store.airtel_money_code)}`,
        timezone: formatTimezone(store),
        alert: `Alert: ${formatAlertHour(store.inventory_alert_send_hour)}`,
        owner: `Owner: ${formatValue(store.owner_id)}`,
        group: `Group: ${formatValue(store.default_group_id)}`,
        exactMatch: `Exact match: ${formatBoolean(store.service_name_exact_match)}`,
        email: store.email || "Not set",
        status: store.email_verified ? "Verified" : "Pending",
        updatedAt: formatDate(store.email_verified_at),
        createdAt: `Created: ${formatDate(store.created_at)}`,
        actions: "",
      }))
    );

    const getUserActions = (user) => {
      return [
        {
          label: "Send code",
          icon: "send",
          tone: "neutral",
          disabled: userActionLoading.value === user.id,
          value: "send-user-code",
          handler: () => requestUserVerification(user),
        },
        {
          label: "Update email",
          icon: "edit-2",
          tone: "neutral",
          value: "update-user-email",
          handler: () => openEditUserEmail(user),
        },
        {
          label: "Remove email",
          icon: "trash-2",
          tone: "danger",
          value: "remove-user-email",
          handler: () => openRemoveUserEmail(user),
        },
      ];
    };

    const handleActionMenuSelect = (item) => {
      if (typeof item?.handler === "function") {
        item.handler();
      }
    };

    const openEbmInitModal = async (store) => {
      if (!store?.id) {
        return;
      }
      ebmReinitContext.value = {
        storeId: store.id,
        storeName: store.name || `Store #${store.id}`,
        ebmSettings: null,
        loading: false,
        loadingMessage: "",
        error: "",
        errorDetails: null,
        successMessage: "",
        responseDetails: null,
      };

      // Fetch EBM settings
      try {
        ebmReinitContext.value.loading = true;
        ebmReinitContext.value.loadingMessage =
          "Fetching store EBM settings...";
        ebmReinitContext.value.error = "";
        ebmReinitContext.value.errorDetails = null;
        const settings = await getStoreEbmSettings(store.id);
        ebmReinitContext.value.ebmSettings = settings;

        // If no EBM settings exist, show error
        if (!settings) {
          ebmReinitContext.value.error =
            "No EBM settings found for this store. Please configure EBM settings before initializing.";
          ebmReinitContext.value.errorDetails = {
            statusCode: 404,
            details: "EBM settings must be configured first",
          };
        }
      } catch (error) {
        console.error("Failed to fetch EBM settings", error);
        const errorResponse = error?.response?.data;
        // If 404, EBM settings don't exist
        if (error?.response?.status === 404) {
          ebmReinitContext.value.error =
            "No EBM settings found for this store. Please configure EBM settings before initializing.";
        } else {
          ebmReinitContext.value.error =
            errorResponse?.error?.message ||
            error?.message ||
            "Unable to fetch store EBM settings.";
        }
        ebmReinitContext.value.errorDetails = {
          statusCode: error?.response?.status,
          details: errorResponse?.error?.details || null,
        };
      } finally {
        ebmReinitContext.value.loading = false;
        ebmReinitContext.value.loadingMessage = "";
      }
    };

    const openEbmInitStandalone = (store) => {
      closeStoreManagementModal();
      openEbmInitModal(store);
    };

    const closeEbmInitModal = () => {
      ebmReinitContext.value = null;
    };

    const confirmEbmInitialize = async (data) => {
      if (!ebmReinitContext.value) {
        return;
      }

      // Check if EBM settings exist
      if (!ebmReinitContext.value.ebmSettings) {
        ebmReinitContext.value.error =
          "No EBM settings found. Cannot initialize EBM.";
        return;
      }

      ebmReinitContext.value.loading = true;
      ebmReinitContext.value.loadingMessage = data.force
        ? "Force initializing EBM..."
        : "Initializing EBM...";
      ebmReinitContext.value.error = "";
      ebmReinitContext.value.errorDetails = null;
      ebmReinitContext.value.successMessage = "";
      ebmReinitContext.value.responseDetails = null;

      try {
        const response = await initializeEbmForStore(
          ebmReinitContext.value.storeId,
          data.force || false
        );

        // Handle success response - response is the full axios response object
        const responseData = response?.data || {};
        ebmReinitContext.value.successMessage =
          responseData?.message || "EBM initialized successfully.";

        // Extract response details if available (from RRA API response)
        if (responseData?.resultCd || responseData?.resultMsg) {
          ebmReinitContext.value.responseDetails = {
            resultCd: responseData.resultCd,
            resultMsg: responseData.resultMsg,
          };
        } else if (
          responseData?.data?.resultCd ||
          responseData?.data?.resultMsg
        ) {
          // Handle nested response structure
          ebmReinitContext.value.responseDetails = {
            resultCd: responseData.data.resultCd,
            resultMsg: responseData.data.resultMsg,
          };
        }

        toast.success(ebmReinitContext.value.successMessage);

        // Close modal after a short delay to show success message
        setTimeout(() => {
          closeEbmInitModal();
          loadStores();
        }, 2000);
      } catch (error) {
        console.error("Failed to initialize EBM", error);
        const errorResponse = error?.response?.data;
        const errorData = errorResponse?.error || errorResponse;

        ebmReinitContext.value.error =
          errorData?.message || error?.message || "Unable to initialize EBM.";

        // Extract detailed error information
        ebmReinitContext.value.errorDetails = {
          statusCode: error?.response?.status,
          resultCd: errorData?.resultCd || errorResponse?.resultCd,
          resultMsg: errorData?.resultMsg || errorResponse?.resultMsg,
          details: errorData?.details || null,
        };
      } finally {
        if (ebmReinitContext.value) {
          ebmReinitContext.value.loading = false;
          ebmReinitContext.value.loadingMessage = "";
        }
      }
    };

    return {
      tabsWithTotals,
      activeTab,
      isRefreshing,
      verificationStatusOptions,
      currentFilters,
      userRows,
      userLoading,
      userPagination,
      userActionLoading,
      storeRows,
      userHeaders,
      userHeaderKeyMapping,
      userTableRows,
      storeLoading,
      storePagination,
      storeHeaders,
      storeHeaderKeyMapping,
      storeTableRows,
      storeActionLoading,
      editContext,
      removeContext,
      storeDetailsContext,
      storeManagementContext,
      storeManagementTab,
      storeManagementTabs,
      storeEmailDraft,
      storeEmailError,
      storeEmailSaving,
      storeEmailRemoving,
      confirmStoreEmailRemoval,
      transferPhoneNumber,
      transferResolvedUser,
      transferResolvedUserDisplayName,
      transferResolvedUserInitials,
      transferLookupLoading,
      transferLookupError,
      transferServerError,
      transferUserIsCurrentOwner,
      transferringOwnership,
      canTransferOwnership,
      currentListLoading,
      formatDate,
      formatValue,
      formatBoolean,
      formatStoreType,
      formatTimezone,
      formatAlertHour,
      handleSearchInput,
      handleFilterChange,
      refreshActiveTab,
      changeUserPage,
      changeStorePage,
      handleUserPerPageChange,
      handleStorePerPageChange,
      requestUserVerification,
      requestStoreVerification,
      sendManagedStoreVerification,
      saveManagedStoreEmail,
      removeManagedStoreEmail,
      lookupTransferOwner,
      submitOwnershipTransfer,
      openEditUserEmail,
      openEditStoreEmail,
      closeEditModal,
      saveEmailChange,
      openRemoveUserEmail,
      openRemoveStoreEmail,
      confirmRemoveEmail,
      getUserActions,
      ebmReinitContext,
      ownerDetailsContext,
      loadManagedStoreOwner,
      openEbmInitModal,
      openEbmInitStandalone,
      closeEbmInitModal,
      confirmEbmInitialize,
      openStoreManagementModal,
      closeStoreManagementModal,
      openStoreDetailsModal,
      closeStoreDetailsModal,
      handleActionMenuSelect,
    };
  },
};

function formatDate(value) {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return String(value);
}

function formatBoolean(value) {
  return value ? "Yes" : "No";
}

function formatStoreType(value) {
  if (!value) {
    return "—";
  }
  return String(value)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");
}

function formatTimezone(store) {
  if (!store) {
    return "—";
  }

  const timezoneName = formatValue(store.timezone_name);
  const offsetMinutes = Number(store.timezone_offset_minutes);
  if (Number.isNaN(offsetMinutes)) {
    return timezoneName;
  }

  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, "0");
  const minutes = String(absoluteMinutes % 60).padStart(2, "0");

  return `${timezoneName} (UTC${sign}${hours}:${minutes})`;
}

function formatAlertHour(value) {
  const hour = Number(value);
  if (Number.isNaN(hour) || hour < 0 || hour > 23) {
    return "—";
  }
  return `${String(hour).padStart(2, "0")}:00`;
}
</script>

<style scoped>
.admin-email-table__skeleton {
  display: grid;
  gap: 0.65rem;
  padding: 0.85rem;
}

.admin-email-table__skeleton div {
  animation: admin-email-pulse 1.2s ease-in-out infinite;
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 78%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  min-height: 3.5rem;
}

.admin-email-table__mono {
  color: var(--hs-muted, #64748b);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-weight: 850;
}

.admin-email-table__primary,
.admin-email-table__strong {
  color: var(--hs-text, #0f172a);
  font-weight: 950;
}

.admin-email-table__muted {
  color: var(--hs-muted, #64748b);
  font-size: 0.78rem;
  font-weight: 800;
  margin-top: 0.16rem;
}

.admin-email-table__wrap {
  display: block;
  max-width: 20rem;
  overflow-wrap: anywhere;
  white-space: normal;
}

.admin-email-table__actions {
  display: flex;
  justify-content: flex-end;
}

@keyframes admin-email-pulse {
  0%,
  100% {
    opacity: 0.62;
  }

  50% {
    opacity: 1;
  }
}

.store-management-modal {
  display: grid;
  gap: 1rem;
}

.store-management-modal__section {
  display: grid;
  gap: 1rem;
}

.store-management-modal__summary {
  background: color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 76%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 12px;
  display: grid;
  gap: 0.2rem;
  padding: 0.9rem 1rem;
}

.store-management-modal__summary span {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.store-management-modal__summary strong {
  color: var(--hs-text, #0f172a);
  font-size: 0.94rem;
  font-weight: 900;
}

.store-management-modal__actions-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.store-management-action {
  align-items: flex-start;
  background: var(--hs-surface, #fff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.24));
  border-radius: 12px;
  color: var(--hs-text, #0f172a);
  display: flex;
  gap: 0.75rem;
  min-height: 86px;
  padding: 0.9rem;
  text-align: left;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.store-management-action:hover {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 42%, var(--hs-border, rgba(148, 163, 184, 0.24)));
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}

.store-management-action :deep(svg) {
  color: var(--hs-primary, #3293ec);
  flex: 0 0 auto;
  margin-top: 0.1rem;
}

.store-management-action strong,
.store-management-action small {
  display: block;
}

.store-management-action strong {
  font-size: 0.88rem;
  font-weight: 950;
}

.store-management-action small {
  color: var(--hs-muted, #64748b);
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.4;
  margin-top: 0.2rem;
}

.store-management-action--danger :deep(svg),
.store-management-action--danger strong {
  color: var(--hs-danger, #dc2626);
}

.store-management-inline-card {
  background: var(--hs-surface, #fff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.24));
  border-radius: 14px;
  display: grid;
  gap: 1rem;
  padding: 1rem;
}

.store-management-inline-card__header {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.store-management-inline-card__header h4 {
  color: var(--hs-text, #0f172a);
  font-size: 0.96rem;
  font-weight: 950;
  margin: 0;
}

.store-management-inline-card__header p {
  color: var(--hs-muted, #64748b);
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.45;
  margin: 0.25rem 0 0;
}

.store-management-inline-form {
  align-items: end;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(0, 1fr) auto;
}

.store-management-inline-form--three {
  grid-template-columns: minmax(0, 1fr) auto auto;
}

.store-management-inline-actions {
  display: flex;
  justify-content: flex-end;
}

.store-management-alert {
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1.45;
  padding: 0.75rem 0.85rem;
}

.store-management-alert--danger {
  background: color-mix(in srgb, var(--hs-danger, #dc2626) 9%, var(--hs-surface, #fff));
  border: 1px solid color-mix(in srgb, var(--hs-danger, #dc2626) 28%, transparent);
  color: var(--hs-danger, #dc2626);
}

.store-management-user-card {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 72%, var(--hs-surface, #fff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  display: grid;
  gap: 0.8rem;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 0.85rem;
}

.store-management-user-card__avatar {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 12%, var(--hs-surface, #fff));
  border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 22%, transparent);
  border-radius: 999px;
  color: var(--hs-primary, #3293ec);
  display: flex;
  font-size: 0.82rem;
  font-weight: 950;
  height: 2.5rem;
  justify-content: center;
  width: 2.5rem;
}

.store-management-user-card strong,
.store-management-user-card span,
.store-management-user-card small {
  display: block;
}

.store-management-user-card strong {
  color: var(--hs-text, #0f172a);
  font-size: 0.9rem;
  font-weight: 950;
}

.store-management-user-card span,
.store-management-user-card small {
  color: var(--hs-muted, #64748b);
  font-size: 0.76rem;
  font-weight: 750;
  overflow-wrap: anywhere;
}

.store-management-danger-zone {
  align-items: center;
  background: color-mix(in srgb, var(--hs-danger, #dc2626) 5%, var(--hs-surface, #fff));
  border: 1px solid color-mix(in srgb, var(--hs-danger, #dc2626) 18%, var(--hs-border, rgba(148, 163, 184, 0.24)));
  border-radius: 12px;
  color: var(--hs-muted, #64748b);
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  padding: 0.8rem;
}

.store-management-danger-zone span {
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.4;
}

.store-management-danger-zone__actions {
  display: flex;
  gap: 0.5rem;
}

@media (max-width: 720px) {
  .store-management-inline-card__header,
  .store-management-danger-zone {
    align-items: stretch;
    flex-direction: column;
  }

  .store-management-inline-form,
  .store-management-inline-form--three {
    align-items: stretch;
    grid-template-columns: 1fr;
  }

  .store-management-inline-actions,
  .store-management-danger-zone__actions {
    justify-content: flex-start;
  }

  .store-management-user-card {
    align-items: start;
    grid-template-columns: auto minmax(0, 1fr);
  }

  .store-management-user-card :deep(.hs-badge) {
    grid-column: 1 / -1;
    justify-self: start;
  }
}

.owner-details {
  display: grid;
  gap: 1rem;
}

.owner-details__state {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 65%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  color: var(--hs-muted, #64748b);
  font-size: 0.9rem;
  font-weight: 850;
  padding: 1.25rem;
}

.owner-details__state--error {
  background: color-mix(in srgb, var(--hs-danger, #b91c1c) 9%, var(--hs-surface, #ffffff));
  border-color: color-mix(in srgb, var(--hs-danger, #b91c1c) 24%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  color: var(--hs-danger, #b91c1c);
}

.owner-details__content {
  display: grid;
  gap: 0.85rem;
}

.owner-details__grid {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.owner-details__item {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 68%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  display: grid;
  gap: 0.35rem;
  min-width: 0;
  padding: 0.9rem;
}

.owner-details__item span {
  color: var(--hs-muted, #64748b);
  font-size: 0.7rem;
  font-weight: 950;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.owner-details__item strong {
  color: var(--hs-text, #0f172a);
  font-size: 0.92rem;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.owner-details__item small {
  color: var(--hs-muted, #64748b);
  font-size: 0.76rem;
  font-weight: 800;
}

@media (max-width: 560px) {
  .owner-details__grid {
    grid-template-columns: 1fr;
  }
}
</style>
